import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Tooltip } from "../ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Loader2Icon, AlertCircleIcon, Info } from "lucide-react";
import { usePermissions } from "../../PermissionsProvider";
import { AgentService } from "../../services/AgentService";
import { Agent } from "../../model_gen/agent_base";

interface AgentEditModalProps {
  open: boolean;
  onClose: () => void;
  onAgentUpdated?: (updated?: Agent) => void;
  agent: Agent;
}

const AgentEditModal: React.FC<AgentEditModalProps> = ({
  open,
  onClose,
  onAgentUpdated,
  agent,
}) => {
  const { apiKey } = usePermissions();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [baseUrlError, setBaseUrlError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    agent_name: "",
    description: "",
    base_url: "",
  });

  useEffect(() => {
    if (agent) {
      setFormData({
        agent_name: agent.agent_name || "",
        description: agent.description || "",
        base_url: (agent.agent_config as any)?.base_url || "",
      });
    }
  }, [agent]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.agent_name.trim()) {
      setError("Agent name is required");
      return;
    }

    // Validate base_url if provided
    if (formData.base_url && formData.base_url.trim()) {
      try {
        new URL(formData.base_url);
      } catch (error) {
        setError("Invalid base URL format");
        return;
      }
    }

    if (!apiKey) {
      setError("Missing authentication");
      return;
    }

    setIsLoading(true);
    setError("");

    // Merge agent_config with updates for base_url
    const mergedAgentConfig: any = { ...(agent.agent_config as any) };
    const trimmedBaseUrl = (formData.base_url || "").trim();

    if (trimmedBaseUrl) {
      mergedAgentConfig.base_url = trimmedBaseUrl;
    } else {
      if (mergedAgentConfig && "base_url" in mergedAgentConfig) {
        delete mergedAgentConfig.base_url;
      }
    }

    const agentData = {
      agent_name: formData.agent_name.trim(),
      description: formData.description.trim() || null,
      agent_config: mergedAgentConfig,
      meta: agent.meta || {},
      status: agent.status || "ENABLED",
    };

    AgentService.updateAgent(agent.id, agentData, setIsLoading, apiKey).subscribe(
      (updatedAgent: Agent) => {
        if (onAgentUpdated) {
          onAgentUpdated(updatedAgent);
        }
        handleClose();
      },
      (error: any) => {
        console.error("Error updating agent:", error);
        setIsLoading(false);
        setError(error.message || "Failed to update agent");
      }
    );
  };

  const handleClose = () => {
    setError("");
    onClose();
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError("");
    
    // Validate base_url on change
    if (field === "base_url") {
      if (value && value.trim()) {
        try {
          new URL(value);
          setBaseUrlError(null);
        } catch (error) {
          setBaseUrlError("Please enter a valid URL (e.g., https://api.example.com)");
        }
      } else {
        setBaseUrlError(null);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Agent</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <AlertCircleIcon className="w-4 h-4 text-destructive" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="agent_name">
              Agent Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="agent_name"
              placeholder="Enter agent name"
              value={formData.agent_name}
              onChange={(e) => handleInputChange("agent_name", e.target.value)}
              maxLength={100}
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Optional description for this agent"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              maxLength={500}
              rows={3}
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="base_url">Base URL (Optional)</Label>
              <Tooltip content="Default base URL for all API actions. Individual actions can override this URL if needed. Leave empty to use full URLs in action configurations.">
                <Info className="w-4 h-4 text-muted-foreground hover:text-foreground transition-colors cursor-help" />
              </Tooltip>
            </div>
            <Input
              id="base_url"
              type="url"
              placeholder="https://api.example.com"
              value={formData.base_url}
              onChange={(e) => handleInputChange("base_url", e.target.value)}
              disabled={isLoading}
            />
            {baseUrlError && (
              <p className="text-sm text-red-500 mt-1">{baseUrlError}</p>
            )}
          </div>


          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !formData.agent_name.trim()}>
              {isLoading && <Loader2Icon className="w-4 h-4 mr-2 animate-spin" />}
              Update Agent
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AgentEditModal; 